import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { isOAKant } from '@/lib/rollen';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { PERMISSIONS } from '../lib/permissions';

export type SearchCategory =
  | 'projects'
  | 'leads'
  | 'contacts'
  | 'emails'
  | 'tasks'
  | 'purchase_invoices'
  | 'sales_invoices';

export interface SearchResult {
  id: string;
  category: SearchCategory;
  title: string;
  subtitle: string | null;
  meta: string | null;
}

interface UseGlobalSearchReturn {
  query: string;
  setQuery: (q: string) => void;
  results: SearchResult[];
  loading: boolean;
  activeCategories: SearchCategory[];
}

const DEBOUNCE_MS = 300;
const RESULTS_PER_CATEGORY = 5;
const MIN_QUERY_LENGTH = 2;

function getAllowedCategories(
  role: string | undefined,
  can: (key: string) => boolean,
): SearchCategory[] {
  if (!role) return [];

  if (role === 'Adviseur') return ['leads'];

  // De hele onderaannemerskant, dus ook een medewerker. Stond hier alleen
  // 'Onderaannemer', dan viel een medewerker door naar de interne categorieën
  // en kreeg hij contacten in zijn zoekresultaten.
  if (isOAKant(role)) return ['projects'];

  const cats: SearchCategory[] = ['projects', 'contacts'];

  if (can(PERMISSIONS.LEADS_VIEW)) cats.push('leads');

  if (can(PERMISSIONS.EMAIL_INBOX_FULL) || can(PERMISSIONS.EMAIL_PERSONAL_INBOX))
    cats.push('emails');

  if (role !== 'Administratie') cats.push('tasks');

  if (can(PERMISSIONS.PURCHASE_INVOICES_VIEW)) cats.push('purchase_invoices');
  if (can(PERMISSIONS.SALES_INVOICES_VIEW)) cats.push('sales_invoices');

  return cats;
}

async function searchProjects(term: string): Promise<SearchResult[]> {
  const pattern = `%${term}%`;
  const { data } = await supabase
    .from('projects')
    .select('id, project_number, name, client_name, status')
    .or(
      `name.ilike.${pattern},project_number.ilike.${pattern},client_name.ilike.${pattern},address.ilike.${pattern}`,
    )
    .order('updated_at', { ascending: false })
    .limit(RESULTS_PER_CATEGORY);

  return (data || []).map((p) => ({
    id: p.id,
    category: 'projects' as const,
    title: p.name,
    subtitle: p.client_name,
    meta: `${p.project_number} · ${p.status}`,
  }));
}

async function searchLeads(term: string): Promise<SearchResult[]> {
  const pattern = `%${term}%`;
  const { data } = await supabase
    .from('leads')
    .select('id, lead_number, contact_name, company, email, status, address, plaats')
    .eq('is_archived', false)
    .or(
      `lead_number.ilike.${pattern},contact_name.ilike.${pattern},company.ilike.${pattern},email.ilike.${pattern},work_description.ilike.${pattern},address.ilike.${pattern},plaats.ilike.${pattern},phone.ilike.${pattern}`,
    )
    .order('updated_at', { ascending: false })
    .limit(RESULTS_PER_CATEGORY);

  return (data || []).map((l) => ({
    id: l.id,
    category: 'leads' as const,
    title: l.contact_name,
    subtitle: [l.address, l.plaats].filter(Boolean).join(', ') || l.company,
    meta: `${l.lead_number} · ${l.status}`,
  }));
}

async function searchContacts(term: string): Promise<SearchResult[]> {
  const pattern = `%${term}%`;
  const { data } = await supabase
    .from('contacts')
    .select('id, name, company, email, phone')
    .or(
      `name.ilike.${pattern},company.ilike.${pattern},email.ilike.${pattern},phone.ilike.${pattern}`,
    )
    .order('name')
    .limit(RESULTS_PER_CATEGORY);

  return (data || []).map((c) => ({
    id: c.id,
    category: 'contacts' as const,
    title: c.name,
    subtitle: c.company,
    meta: c.email || c.phone,
  }));
}

async function searchEmails(term: string): Promise<SearchResult[]> {
  const pattern = `%${term}%`;
  const { data } = await supabase
    .from('incoming_emails')
    .select('id, from_name, from_email, subject, received_at')
    .or(`from_name.ilike.${pattern},from_email.ilike.${pattern},subject.ilike.${pattern}`)
    .order('received_at', { ascending: false })
    .limit(RESULTS_PER_CATEGORY);

  return (data || []).map((e) => ({
    id: e.id,
    category: 'emails' as const,
    title: e.subject || '(geen onderwerp)',
    subtitle: e.from_name || e.from_email,
    meta: e.received_at ? new Date(e.received_at).toLocaleDateString('nl-NL') : null,
  }));
}

async function searchTasks(term: string): Promise<SearchResult[]> {
  const pattern = `%${term}%`;
  const { data } = await supabase
    .from('employee_tasks')
    .select('id, title, contact_name, status, due_date')
    .or(`title.ilike.${pattern},description.ilike.${pattern},contact_name.ilike.${pattern}`)
    .order('created_at', { ascending: false })
    .limit(RESULTS_PER_CATEGORY);

  return (data || []).map((t) => ({
    id: t.id,
    category: 'tasks' as const,
    title: t.title,
    subtitle: t.contact_name,
    meta: t.status,
  }));
}

async function searchPurchaseInvoices(term: string): Promise<SearchResult[]> {
  const pattern = `%${term}%`;
  const { data } = await supabase
    .from('purchase_invoices')
    .select('id, invoice_number, supplier, amount, status')
    .or(`supplier.ilike.${pattern},invoice_number.ilike.${pattern}`)
    .order('created_at', { ascending: false })
    .limit(RESULTS_PER_CATEGORY);

  return (data || []).map((i) => ({
    id: i.id,
    category: 'purchase_invoices' as const,
    title: i.supplier || i.invoice_number || 'Inkoopfactuur',
    subtitle: i.invoice_number,
    meta: i.status,
  }));
}

async function searchSalesInvoices(term: string): Promise<SearchResult[]> {
  const pattern = `%${term}%`;
  const { data } = await supabase
    .from('sales_invoices')
    .select('id, invoice_number, status, total_amount')
    .or(`invoice_number.ilike.${pattern}`)
    .order('created_at', { ascending: false })
    .limit(RESULTS_PER_CATEGORY);

  return (data || []).map((i) => ({
    id: i.id,
    category: 'sales_invoices' as const,
    title: i.invoice_number || 'Verkoopfactuur',
    subtitle: i.total_amount != null ? `€${Number(i.total_amount).toLocaleString('nl-NL')}` : null,
    meta: i.status,
  }));
}

const searchFns: Record<SearchCategory, (term: string) => Promise<SearchResult[]>> = {
  projects: searchProjects,
  leads: searchLeads,
  contacts: searchContacts,
  emails: searchEmails,
  tasks: searchTasks,
  purchase_invoices: searchPurchaseInvoices,
  sales_invoices: searchSalesInvoices,
};

export function useGlobalSearch(): UseGlobalSearchReturn {
  const { profile } = useAuth();
  const { can } = usePermissions();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(0);

  const activeCategories = useMemo(
    () => getAllowedCategories(profile?.role, can),
    [profile?.role, can],
  );

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const id = ++abortRef.current;

    const timer = setTimeout(async () => {
      try {
        const promises = activeCategories.map((cat) =>
          searchFns[cat](trimmed).catch(() => [] as SearchResult[]),
        );
        const grouped = await Promise.all(promises);
        if (abortRef.current !== id) return;
        setResults(grouped.flat());
      } catch {
        if (abortRef.current === id) setResults([]);
      } finally {
        if (abortRef.current === id) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, activeCategories]);

  const stableSetQuery = useCallback((q: string) => setQuery(q), []);

  return { query, setQuery: stableSetQuery, results, loading, activeCategories };
}
